import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { EngineState } from 'src/app/engine/state/engine.state';
import { Observable, Subject } from 'rxjs';
import { ViewTreeNode } from 'src/app/engine/models/view.tree-node';
import { SetObjectVisibility, SetObjectPickable } from 'src/app/edit/state/edit.actions';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { takeUntil } from 'rxjs/operators';
import { EngineNodesState } from 'src/app/engine/state/nodes/engine.nodes.state';
import { SetCheckedNodes, CheckInverseNodes, CheckAllNodes } from 'src/app/engine/state/nodes/engine.nodes.actions';

@Component({
  selector: 'app-checked-nodes',
  templateUrl: './checked-nodes.component.html',
  styleUrls: ['./checked-nodes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckedNodesComponent implements OnInit, OnDestroy {
  @Select(EngineNodesState.getCheckedNodes)
  checkedNodes$: Observable<ViewTreeNode[]>;

  leaves: string[] = [];
  count = 0;
  
  private ngUnsubscribe = new Subject();

  constructor(
    private store: Store,
    private graphService: BdGraphService,
    private changeDetector: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.checkedNodes$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((nodes) => {
        this.leaves = this.getLeaves(nodes);
        this.count = this.leaves.length;
        this.changeDetector.markForCheck();
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  getLeaves(nodes: ViewTreeNode[]): string[] {
    if (!nodes) {
      return [];
    }

    return this.graphService.getAllLeafDescendants(
      this.store.selectSnapshot(EngineState.getViewGraph), 
      nodes.map(node => node.id)
    );
  }

  handleClearSelection() {
    this.store.dispatch(new SetCheckedNodes([]));
  } 

  handleBulkShow() {
    this.store.dispatch(new SetObjectVisibility(this.store.selectSnapshot(EngineNodesState.getCheckedNodes), true));
  }

  handleBulkHide() {
    this.store.dispatch(new SetObjectVisibility(this.store.selectSnapshot(EngineNodesState.getCheckedNodes), false));
  }

  handleSelectInverse() {
    this.store.dispatch(new CheckInverseNodes(this.store.selectSnapshot(EngineNodesState.getCheckedNodes)));  
  }

  handleHideInverse() {
    const nodes = this.getInverseSelected();
    this.store.dispatch(new SetObjectVisibility(nodes, false));
  }

  handleSelectAll() {
    this.store.dispatch(new CheckAllNodes());
  }

  handleBulkPickable(pickable: boolean) {
    const nodes = this.store.selectSnapshot(EngineNodesState.getCheckedNodes);
    this.store.dispatch(new SetObjectPickable(nodes, pickable));
  }

  handleBulkInversePickable(pickable: boolean) {
    const nodes = this.getInverseSelected();
    this.store.dispatch(new SetObjectPickable(nodes, pickable));
  }

  private getInverseSelected(): ViewTreeNode[] {
    const viewgraph = this.store.selectSnapshot(EngineState.getViewGraph);
    const nodeIds = this.store.selectSnapshot(EngineNodesState.getCheckedNodes).map(node => node.id);
    const inverseNodeIds = this.graphService.getInverseSelection(viewgraph, nodeIds);
    return this.graphService.getNodes(viewgraph, inverseNodeIds);
  }
}
